/* eslint-disable prettier/prettier */
import React, { useState, useRef, ChangeEvent, FormEvent } from 'react';
import styles from '../../../../styles/components/pageComponents/learner/LogCoachingHours.module.css';
import { useCoachingHours } from '../../../../hooks/useCoachingHours';
import SuccessContent from '../../../controlVariations/ModularSuccessModal';
import ErrorPopupModal from '../../../controlVariations/ErrorPopupMoadal';
import ResponsiveModal from '../../../controlVariations/ResponsiveModal';
import { TimePicker } from '../../../common/TimePicker';

interface FormData {
	fullName: string;
	date: string;
	startTime: string;
	endTime: string;

	// startHH: string;
	// startMM: string;
	// endHH: string;
	// endMM: string;

	file: File | null;
}

interface FormErrors {
	fullName?: string;
	date?: string;
	time?: string;
	file?: string;
}

export interface CoachingHoursFormData {
	fullName: string;
	date: string;
	startTime: string;
	endTime: string;
	file: File | null;
}

interface LogCoachingHoursProps {
	onClose: () => void;

	uploadProgress: number;
	isUploading: boolean;

	learnerId?: string;
	programId?: string;

	onSubmit: (data: CoachingHoursFormData) => Promise<void>;
}

export default function LogCoachingHours({
	onClose,
	learnerId,
	programId,
	onSubmit,
	uploadProgress,
	isUploading
}: LogCoachingHoursProps) {
	//
	// -------------------------------------------
	// HOOK
	// -------------------------------------------
	//

	const { loading, error, uploadFile, createLog } = useCoachingHours();

	const [formData, setFormData] = useState<FormData>({
		fullName: '',
		date: '',
		startTime: '00:00',
		endTime: '00:00',
		// startHH: '',
		// startMM: '',
		// endHH: '',
		// endMM: '',
		file: null,
	});

	const [errors, setErrors] = useState<FormErrors>({});

	const ALLOWED_EXTENSIONS = [
		'.mp3',
		'.wav',
		'.mp4',
		'.pdf',
		'.doc',
		'.docx',
		'.txt',
	];

	const ACCEPT_STRING = ALLOWED_EXTENSIONS.join(',');

	const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200 MB
	const MAX_FILE_SIZE_LABEL = '200 MB';

	//
	// Upload Progress
	//

	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;

		// Handle Time digits parsing constraint (max 2 characters, numbers only)
		if (['startHH', 'startMM', 'endHH', 'endMM'].includes(name)) {
			const sanitized = value.replace(/\D/g, '').slice(0, 2);

			// Basic input bounds checking
			if (name.endsWith('HH') && parseInt(sanitized, 10) > 23) return;
			if (name.endsWith('MM') && parseInt(sanitized, 10) > 59) return;

			setFormData(prev => ({ ...prev, [name]: sanitized }));
			setErrors(prev => ({ ...prev, time: undefined }));
			return;
		}

		setFormData(prev => ({
			...prev,
			[name]: value,
		}));

		if (name === 'date') {
			const selectedDate = new Date(value);
			selectedDate.setHours(0, 0, 0, 0);

			const today = new Date();
			today.setHours(0, 0, 0, 0);

			if (selectedDate >= today) {
				setErrors(prev => ({
					...prev,
					date: 'Date must be a past date. Today and future dates are not allowed.',
				}));
			} else {
				setErrors(prev => ({
					...prev,
					date: undefined,
				}));
			}

			return;
		}

		if (
			errors[name as keyof FormErrors] ||
			name === 'startTime' ||
			name === 'endTime'
		) {
			setErrors(prev => ({
				...prev,
				[name]: undefined,
				time: undefined,
			}));
		}
	};

	// const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
	//   if (e.target.files && e.target.files.length > 0) {
	//     setFormData((prev) => ({ ...prev, file: e.target.files![0] }));
	//   }
	// };

	const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files?.length) return;

		const selectedFile = e.target.files[0];

		// Clear previous file error
		setErrors(prev => ({
			...prev,
			file: undefined,
		}));

		// Validate extension
		const extension =
			'.' + selectedFile.name.split('.').pop()?.toLowerCase();

		if (!ALLOWED_EXTENSIONS.includes(extension)) {
			setErrors(prev => ({
				...prev,
				file: `Only ${ALLOWED_EXTENSIONS.join(', ')} files are allowed.`,
			}));

			setFormData(prev => ({
				...prev,
				file: null,
			}));

			e.target.value = '';
			return;
		}

		// Validate file size
		if (selectedFile.size > MAX_FILE_SIZE) {
			setErrors(prev => ({
				...prev,
				file: `File size cannot exceed ${MAX_FILE_SIZE_LABEL}.`,
			}));

			setFormData(prev => ({
				...prev,
				file: null,
			}));

			e.target.value = '';
			return;
		}

		// Valid file
		setFormData(prev => ({
			...prev,
			file: selectedFile,
		}));
	};

	const handleUploadClick = () => {
		fileInputRef.current?.click();
	};

	// Helper formatting utility to map values back into "HH:MM"
	const formatTimeStr = (hh: string, mm: string) => {
		const paddedHH = hh.padStart(2, '0');
		const paddedMM = mm.padStart(2, '0');
		return `${paddedHH}:${paddedMM}`;
	};

	const validateForm = (): boolean => {
		const newErrors: FormErrors = {};
		let isValid = true;

		// Full Name
		if (!formData.fullName.trim()) {
			newErrors.fullName = 'Full name is required';
			isValid = false;
		}

		// Date
		if (!formData.date) {
			newErrors.date = 'Date is required';
			isValid = false;
		} else {
			const selectedDate = new Date(formData.date);
			selectedDate.setHours(0, 0, 0, 0);

			const today = new Date();
			today.setHours(0, 0, 0, 0);

			if (selectedDate >= today) {
				newErrors.date =
					'Date must be a past date. Today and future dates are not allowed.';
				isValid = false;
			}
		}

		// Time Validation
		if (!formData.startTime || !formData.endTime) {
			newErrors.time = 'Start and end time are required';
			isValid = false;
		} else {
			const start = new Date(`1970-01-01T${formData.startTime}`);
			const end = new Date(`1970-01-01T${formData.endTime}`);

			if (end <= start) {
				newErrors.time = 'End time must be after start time';
				isValid = false;
			}
		}

		setErrors(newErrors);
		return isValid;
	};

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		// Convert individual state parameters cleanly into standard output schema format
		const formattedPayload: CoachingHoursFormData = {
			fullName: formData.fullName,
			date: formData.date,
			startTime: formData.startTime,
			endTime: formData.endTime,
			file: formData.file,
		};

		await onSubmit(formattedPayload);
	};

	const yesterday = new Date();
	yesterday.setDate(yesterday.getDate() - 1);

	const maxDate = yesterday.toISOString().split('T')[0];

	//
	// -------------------------------------------
	// JSX
	// -------------------------------------------
	//

	return (
		<div className={styles.modalContainer}>
			{/* Header */}
			<div className={styles.header}>
				<div>
					<h2 className={styles.title}>Log Coaching Hours</h2>
					<p className={styles.subtitle}>
						Fill in the details of your coaching sessions.
					</p>
				</div>
				<button
					className={styles.closeButton}
					aria-label="Close modal"
					onClick={onClose}
				>
					<svg
						width="24"
						height="24"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<line x1="18" y1="6" x2="6" y2="18"></line>
						<line x1="6" y1="6" x2="18" y2="18"></line>
					</svg>
				</button>
			</div>

			{/* Form */}
			<form onSubmit={handleSubmit} className={styles.form}>
				{/* Full Name */}
				<div className={styles.formGroup}>
					<label className={styles.label}>
						Full name<span className={styles.required}>*</span>
					</label>
					<input
						type="text"
						name="fullName"
						placeholder="John Doe"
						value={formData.fullName}
						onChange={handleInputChange}
						className={`${styles.input} ${errors.fullName ? styles.inputError : ''}`}
					/>
					{errors.fullName && (
						<span className={styles.errorMessage}>
							{errors.fullName}
						</span>
					)}
				</div>

				<div className={styles.row}>
					{/* Date */}
					<div className={styles.formGroup}>
						<label className={styles.label}>
							Date<span className={styles.required}>*</span>
						</label>
						<div className={styles.inputWithIcon}>
							<svg
								className={styles.icon}
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<rect
									x="3"
									y="4"
									width="18"
									height="18"
									rx="2"
									ry="2"
								></rect>
								<line x1="16" y1="2" x2="16" y2="6"></line>
								<line x1="8" y1="2" x2="8" y2="6"></line>
								<line x1="3" y1="10" x2="21" y2="10"></line>
							</svg>
							<input
								type="date"
								name="date"
								value={formData.date}
								onChange={handleInputChange}
								className={`${styles.input} ${errors.date ? styles.inputError : ''}`}
								max={maxDate}
							/>
						</div>
						{errors.date && (
							<span className={styles.errorMessage}>
								{errors.date}
							</span>
						)}
					</div>

					{/* Time / Number of Hours Block matching design */}
					<div className={styles.formGroup}>
						<label className={styles.label}>
							Number of Hours
							<span className={styles.required}>*</span>
						</label>

						<div className={styles.timeSelector}>
							<svg
								className={styles.icon}
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<circle cx="12" cy="12" r="10" />
								<polyline points="12 6 12 12 16 14" />
							</svg>

							<TimePicker
								value={formData.startTime}
								onChange={(value) =>
									setFormData(prev => ({
										...prev,
										startTime: value,
										endTime:
											prev.endTime <= value
												? value
												: prev.endTime,
									}))
								}
							/>

							<span className={styles.timeDivider}>to</span>

							<TimePicker
								value={formData.endTime}
								minTime={formData.startTime}
								onChange={(value) =>
									setFormData(prev => ({
										...prev,
										endTime: value,
									}))
								}
							/>
						</div>

						{errors.time && (
							<span className={styles.errorMessage}>
								{errors.time}
							</span>
						)}
					</div>
				</div>

				{/* Recording/Transcript Upload */}
				<div className={styles.formGroup}>
					<label className={styles.label}>Recording/Transcript</label>
					<div
						className={`${styles.uploadArea} ${isUploading ? styles.uploadDisabled : ''}`}
						onClick={!isUploading ? handleUploadClick : undefined}
					>
						<svg
							width="20"
							height="20"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						>
							<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
							<polyline points="17 8 12 3 7 8"></polyline>
							<line x1="12" y1="3" x2="12" y2="15"></line>
						</svg>
						<span>
							{formData.file ? formData.file.name : 'Upload file'}
						</span>
					</div>
					{errors.file && (
						<span className={styles.errorMessage}>
							{errors.file}
						</span>
					)}
					{loading && uploadProgress > 0 && uploadProgress < 100 && (
						<div className={styles.progressContainer}>
							<div className={styles.progressBarWrapper}>
								<div
									className={styles.progressBar}
									style={{
										width: `${uploadProgress}%`,
									}}
								/>
							</div>

							<span className={styles.progressText}>
								Uploading... {uploadProgress}%
							</span>
						</div>
					)}
					{/* Hidden native file input */}
					<input
						type="file"
						ref={fileInputRef}
						onChange={handleFileChange}
						style={{ display: 'none' }}
						accept={ACCEPT_STRING}
					/>
				</div>

				{/* Actions */}
				<div className={styles.footer}>
					<button
						type="submit"
						className={styles.submitButton}
						disabled={isUploading}
					>
						{isUploading
							? `Uploading... ${uploadProgress}%`
							: 'Submit'}
					</button>
				</div>
			</form>
		</div>
	);
}
